import { useEffect, useRef } from 'react';

import { useAuthMe } from 'shared/hooks/auth';
import { GTM } from 'shared/utils/gtm';

/**
 * Syncs authenticated user, customer and subscription data to GTM
 * whenever the authMe query data changes.
 *
 * Deduplicates pushes by comparing key GTM fields.
 */
export const useSyncAuthMeToGTM = (): void => {
  const { data, isSuccess } = useAuthMe();
  const lastPushedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isSuccess || !data) return;

    // Key covers the most frequently changing fields.
    // Customer profile fields (country, businessType, clientRange) are excluded.
    const key = `${data.user.userId}:${data.subscription.subscriptionName}:${data.subscription.status}`;

    if (lastPushedKeyRef.current === key) return;

    lastPushedKeyRef.current = key;
    GTM.pushAuthData(data);
  }, [isSuccess, data]);
};

/**
 * Hook for Google Tag Manager integration.
 * @returns GTM methods for tracking events and managing user data
 *
 * @example
 * const { pushEvent } = useGTM();
 * pushEvent('buttonClicked', { buttonId: 'submit' });
 */
export const useGTM = () => ({
  pushEvent: GTM.pushEvent,
  pushAuthData: GTM.pushAuthData,
  buildAuthData: GTM.buildAuthData,
  clearAuthData: GTM.clearAuthData
});
